import type { ReactElement, SVGProps } from "react";
import type { AccessLevel } from "@bash/shared";

/**
 * AccessLevelGlyph — the per-level mark. Line-art (stroke, currentColor) in the
 * house lucide idiom, so each level reads at a glance and inherits the text
 * color of its row: a guarding shield (Careful), a balance scale (Balanced),
 * calm water (Relaxed), a rocket (Autopilot), tuning sliders (Custom), and a
 * dashed ring (Unknown) that asserts nothing.
 */
export function AccessLevelGlyph({
  kind,
  ...props
}: SVGProps<SVGSVGElement> & {
  kind: AccessLevel | "custom" | "unknown";
}): ReactElement | null {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    ...props,
  };
  switch (kind) {
    case "careful": // guarding shield with a check
      return (
        <svg {...common}>
          <path d="M12 3l7 3v5.5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z" />
          <path d="M9 11.5l2.2 2.2L15.5 9.5" />
        </svg>
      );
    case "balanced": // balance scale
      return (
        <svg {...common}>
          <path d="M12 3.5v17" />
          <path d="M7.5 20.5h9" />
          <path d="M5 7.5h14" />
          <path d="M5 7.5l-2.6 5.2h5.2z" />
          <path d="M19 7.5l-2.6 5.2h5.2z" />
        </svg>
      );
    case "relaxed": // calm water
      return (
        <svg {...common}>
          <path d="M2 7c.6.5 1.2 1 2.5 1C7 8 7 6 9.5 6c2.6 0 2.4 2 5 2C17 8 17 6 19.5 6c1.3 0 1.9.5 2.5 1" />
          <path d="M2 12c.6.5 1.2 1 2.5 1C7 13 7 11 9.5 11c2.6 0 2.4 2 5 2C17 13 17 11 19.5 11c1.3 0 1.9.5 2.5 1" />
          <path d="M2 17c.6.5 1.2 1 2.5 1C7 18 7 16 9.5 16c2.6 0 2.4 2 5 2C17 18 17 16 19.5 16c1.3 0 1.9.5 2.5 1" />
        </svg>
      );
    case "autopilot": // rocket
      return (
        <svg {...common}>
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </svg>
      );
    case "custom": // tuning sliders
      return (
        <svg {...common}>
          <path d="M4 6h9" />
          <path d="M17 6h3" />
          <path d="M4 12h3" />
          <path d="M11 12h9" />
          <path d="M4 18h7" />
          <path d="M15 18h5" />
          <circle cx="15" cy="6" r="2" />
          <circle cx="9" cy="12" r="2" />
          <circle cx="13" cy="18" r="2" />
        </svg>
      );
    case "unknown": // dashed ring: a mark that names no level
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" strokeDasharray="2.4 3.4" />
        </svg>
      );
  }
}
